import React from 'react'

import { useLightMode } from '@/hooks/useLightMode'

import { Box } from '@chakra-ui/react'

import {
  Alert,
  type AlertVariant,
  CloseIcon,
  Box as DarkBox,
  DarkProvider,
  IconButton,
} from '@hivewatch/dark'

/**
 * Props for the StaticBanner component
 */
export interface StaticBannerIProps {
  message: string
  status: AlertVariant
  onDismiss?: () => void
  dismissible?: boolean
  icon?: React.ReactElement
  /** Optional trailing call-to-action. The Alert renders and places it. */
  primaryButton?: { text: string; onClick: () => void }
}

/** Matches the Alert's 22px glyph row so the control sits on the same baseline. */
const DISMISS_BUTTON_SIZE = 22

/** Mirrors the Alert's own horizontal padding. */
const ALERT_INLINE_PADDING = '13px'

/**
 * Static banner component for displaying product messages.
 */
export const StaticBanner = ({
  message,
  status,
  onDismiss,
  dismissible = true,
  icon,
  primaryButton,
}: StaticBannerIProps) => {
  const colorScheme = useLightMode()

  return (
    <Box
      aria-label='Product announcement'
      role='banner'
      width='100%'
      zIndex='banner'
    >
      <DarkProvider>
        <DarkBox position='relative' width='100%'>
          <Alert
            colorScheme={colorScheme}
            icon={
              // react-icons glyphs size to `1em`, so pin the font size to the
              // Alert's glyph box. Color is inherited from the variant.
              icon ? (
                <DarkBox alignItems='center' display='flex' fontSize='22px'>
                  {icon}
                </DarkBox>
              ) : undefined
            }
            isCentered={true}
            primaryButton={primaryButton}
            title={message}
            variant={status}
          />
          {dismissible && onDismiss ? (
            <IconButton
              aria-label='Dismiss announcement'
              colorScheme={colorScheme}
              dimension={DISMISS_BUTTON_SIZE}
              icon={<CloseIcon />}
              onClick={onDismiss}
              position='absolute'
              right={ALERT_INLINE_PADDING}
              top='50%'
              transform='translateY(-50%)'
            />
          ) : null}
        </DarkBox>
      </DarkProvider>
    </Box>
  )
}
